import { MediaContainer } from '@design-system';
import classnames from 'classnames';
import React from 'react';
import SbEditable from 'storyblok-react';

import { Columns, ImageSizes } from '../../../helpers/images';
import {
  ImageStoryblok,
  MediaStoryblok,
  VimeoStoryblok,
} from '../../../types/generated-storyblok';
import { renderStoryblokRichText } from '../../../utils/renderStoryblokRichText';
import { Image } from '../../Image';
import { DynamicLightbox } from '../DynamicLightbox';
import SoundCloud from '../SoundCloud';
import Vimeo from '../Vimeo';

const approximateImageSize = (
  itemSpan: ImageStoryblok['width'],
  colSpan: MediaStoryblok['width'],
): ImageSizes => {
  const imageSpan = (Number(itemSpan) / 12) * Number(colSpan);

  return {
    sm: { ratio: 'intrinsic', span: imageSpan as Columns },
    md: { ratio: 'intrinsic', span: imageSpan as Columns },
    lg: { ratio: 'intrinsic', span: imageSpan as Columns },
  };
};

type Props = {
  blok: MediaStoryblok;
  index?: number;
};

const Media: React.FC<Props> = ({ blok, index }) => {
  return (
    <div className="grid-section pb-8 pt-3 md:pb-9">
      {/* @ts-expect-error require storyblok-react upgrade */}
      <SbEditable content={blok}>
        <div
          className={classnames('col-span-12 col-start-1', {
            'md:col-span-6 md:col-start-4': blok.width === '6',
            'md:col-span-8 md:col-start-3': blok.width === '8',
            'md:col-span-10 md:col-start-2': blok.width === '10',
          })}
        >
          <div className="m-auto grid w-full grid-cols-1 gap-0.5 md:grid-cols-12">
            {blok.mediaItems?.map((mediaItem, key) => (
              <div
                key={key}
                className={classnames('col-span-12', {
                  'md:col-span-1': mediaItem.width === '1',
                  'md:col-span-2': mediaItem.width === '2',
                  'md:col-span-3': mediaItem.width === '3',
                  'md:col-span-4': mediaItem.width === '4',
                  'md:col-span-6': mediaItem.width === '6',
                  'md:col-span-9': mediaItem.width === '9',
                  'md:col-span-12': mediaItem.width === '12',
                })}
              >
                <figure>
                  {mediaItem.component === 'image' &&
                    mediaItem.image.filename &&
                    renderImage(mediaItem, blok.width, index === 0)}
                  {mediaItem.component === 'vimeo' &&
                    mediaItem.video.vimeo_oembed &&
                    renderVideo(mediaItem, blok.width)}
                  {mediaItem.component === 'soundCloud' &&
                    mediaItem.trackId &&
                    renderSoundcloud(mediaItem.trackId)}
                  {mediaItem.component !== 'soundCloud' &&
                    mediaItem?.caption && (
                      <figcaption className="mt-3 text-center text-neutral-4">
                        {renderStoryblokRichText(mediaItem.caption)}
                      </figcaption>
                    )}
                </figure>
              </div>
            ))}
          </div>
        </div>
      </SbEditable>
    </div>
  );
};

export default Media;

const renderImage = (
  { image, width }: ImageStoryblok,
  blokWidth: MediaStoryblok['width'],
  eagerLoading: boolean,
) => {
  return (
    <DynamicLightbox
      item={{
        key: image.id,
        media: { type: 'IMAGE', src: image.filename, alt: image.alt },
        copyright: image && image.copyright,
      }}
    >
      {(lightbox) => (
        <div
          onClick={() => lightbox.openOnSlide(image.id)}
          onKeyDown={() => lightbox.openOnSlide(image.id)}
          role="button"
          tabIndex={0}
        >
          <MediaContainer
            mediaComponent={
              <Image
                alt={image.alt || ''}
                eagerLoading={eagerLoading}
                focus={image?.focus}
                hoverEffect
                size={approximateImageSize(width, blokWidth)}
                src={image.filename}
              />
            }
            type="Image"
          />
        </div>
      )}
    </DynamicLightbox>
  );
};

const renderSoundcloud = (trackId: string) => {
  return <SoundCloud trackId={trackId} />;
};

const renderVideo = (
  mediaItem: VimeoStoryblok,
  blokWidth: MediaStoryblok['width'],
) => {
  const renderWithLightbox = mediaItem.image && mediaItem.image.filename;

  if (renderWithLightbox) return renderVideoInLightbox(mediaItem, blokWidth);
  return renderEmbeddedVideo(mediaItem);
};

const renderEmbeddedVideo = ({ video }: VimeoStoryblok) => {
  return <Vimeo video={video} />;
};

const renderVideoInLightbox = (
  { image, title, video, width }: VimeoStoryblok,
  blokWidth: MediaStoryblok['width'],
) => {
  const vimeoId = video.vimeo_oembed.response.video_id;
  const media = { type: 'VIMEO' as const, key: vimeoId };

  return (
    <DynamicLightbox
      item={{
        key: media.key,
        media: {
          ...media,
          id: vimeoId.toString(),
        },
        copyright: image?.copyright,
      }}
    >
      {(lightbox) => (
        <MediaContainer
          duration={video.vimeo_oembed.response.duration}
          mediaComponent={
            <Image
              alt={image?.alt || ''}
              hoverEffect
              size={approximateImageSize(width, blokWidth)}
              src={image!.filename}
            />
          }
          onClick={() => lightbox.openOnSlide(media.key)}
          title={title}
          type="Video"
        />
      )}
    </DynamicLightbox>
  );
};
